import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Info as InfoIcon, Menu as MenuIcon, SignalCellularAlt as RundownIcon } from "@mui/icons-material";
import { Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import CircleIcon from "svg_icons/circle-with-dot.svg?react";
import HubIcon from "svg_icons/hub-icon.svg?react";
import { useDebouncedCallback } from "use-debounce";

import { hydrateGroup, selectDraftConfigEntities, selectDraftEntityChildrenById, selectVersionMlcId, useUpdateGroupConfigMutation } from "fond/api";
import { AdvancedSettings } from "fond/styleEditor/AdvancedSettings";
import { GroupHeader } from "fond/styleEditor/GroupHeader";
import { GroupConfig, GroupRundown } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";

import { Tab, Tabs } from "../FieldSelection/fieldSelection.styles";
import {
  Container,
  Header,
  InfoContent,
  InfoItem,
  InfoMessage,
  InfoTitle,
  RundownContainer,
  RundownItem,
  RundownLabel,
  RundownValue,
  TabPanel,
} from "./groupEditor.styles";

type TabType = "summary" | "advanced";

interface IProps {
  groupConfig: GroupConfig;
}

const GroupEditor: React.FC<IProps> = ({ groupConfig }: IProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const children = useAppSelector((state) => selectDraftEntityChildrenById(state, groupConfig.ID));
  const mapLayerConfigId = useAppSelector(selectVersionMlcId);
  const [updateGroupConfig] = useUpdateGroupConfigMutation();
  const draft = useSelector(selectDraftConfigEntities);
  const [tabValue, setTabValue] = useState<TabType>("summary");

  const { layers, sublayers, styles }: GroupRundown = useMemo(() => {
    return {
      layers: children.filter((entity) => entity.Type === "LAYER").length,
      sublayers: children.filter((entity) => entity.Type === "SUBLAYER").length,
      styles: children.filter((entity) => entity.Type === "STYLE").length,
    };
  }, [children]);

  const handleKeyChange = useDebouncedCallback((newKey: string) => {
    const newGroup = hydrateGroup(draft, { ...groupConfig, Key: newKey });
    updateGroupConfig({ groupConfig: newGroup, mapLayerConfigId }).catch(() => {
      enqueueSnackbar("Failed to save draft changes", { variant: "error" });
    });
  }, 500);

  return (
    <Container>
      <GroupHeader groupConfig={groupConfig} />
      <Tabs
        value={tabValue}
        onChange={(event, newValue) => {
          setTabValue(newValue);
        }}
      >
        <Tab value="summary" label="Summary" />
        <Tab value="advanced" label="Advanced" />
      </Tabs>
      {tabValue === "summary" && (
        <TabPanel>
          <Header>
            <RundownIcon fontSize="small" />
            <Typography variant="caption" fontWeight={600}>
              Group Rundown
            </Typography>
          </Header>
          <RundownContainer>
            <RundownItem>
              <RundownLabel>TOTAL LAYERS</RundownLabel>
              <RundownValue component="p" variant="h5">
                {layers}
              </RundownValue>
            </RundownItem>
            <RundownItem>
              <RundownLabel>STYLES</RundownLabel>
              <RundownValue component="p" variant="h5">
                {styles}
              </RundownValue>
            </RundownItem>
            <RundownItem>
              <RundownLabel>SUBLAYERS</RundownLabel>
              <RundownValue component="p" variant="h5">
                {sublayers}
              </RundownValue>
            </RundownItem>
          </RundownContainer>
          <Header>
            <InfoIcon fontSize="small" />
            <Typography variant="caption" fontWeight={600}>
              Useful Info
            </Typography>
          </Header>
          <InfoItem>
            <CircleIcon />
            <InfoContent>
              <InfoTitle>Edited Style</InfoTitle>
              <InfoMessage>Icon indicating you’ve edited a style.</InfoMessage>
            </InfoContent>
          </InfoItem>
          <InfoItem>
            <MenuIcon />
            <InfoContent>
              <InfoTitle>Reorder Layer</InfoTitle>
              <InfoMessage>You can reorder layers within a group.</InfoMessage>
            </InfoContent>
          </InfoItem>
          <InfoItem>
            <HubIcon />
            <InfoContent>
              <InfoTitle>Icons</InfoTitle>
              <InfoMessage>Derived from the styles within a layer or sublayer.</InfoMessage>
            </InfoContent>
          </InfoItem>
        </TabPanel>
      )}
      {tabValue === "advanced" && <AdvancedSettings keyValue={groupConfig.Key} onKeyChange={handleKeyChange} />}
    </Container>
  );
};

export default GroupEditor;
